/**
 * The zoom between the library and a map: a card's picture grows into the full plan, and the plan
 * shrinks back into its card — the way a photo opens from its thumbnail.
 *
 * How it is made, and why that way:
 *  - It is a hero laid over the app, not a transition of the pages. The map's editor mounts at once
 *    underneath — untransformed (it measures its stage as it is made), already taking touches
 *    (the hero takes none) — and is shown when the flight lands. A finger is never lost, as it
 *    would be to a WebKit view transition.
 *  - A flight is two transforms and nothing else: a clipped WINDOW that goes from the card's
 *    picture box to the whole screen, and inside it a CAMERA that carries the plan from where the
 *    card shows it (cropped to fill) to exactly where the editor draws it (the same `fitView`).
 *    The camera's transform is the window's undone, which no easing curve can express — so both
 *    are given as a few dozen keyframes sampled from one spring. Transform keyframes run on the
 *    compositor: the flight stays smooth while the main thread is busy opening the map.
 *  - The window's corners go from the card's to square as it grows. Scaling a rounded box scales
 *    its corners with it (and unevenly, as the window changes shape), so the radius is animated
 *    against that, frame by frame — on a clip element of its own inside the window, so that this
 *    one main-thread animation never takes the transforms off the compositor.
 *  - What flies is the editor's own picture. The hero sets off as the card's thumbnail (sharp at
 *    that size), and the moment the editor underneath has drawn the plan — it is warmed from the
 *    finger going down (app/warm.ts), so that is early — a copy of its canvases, backdrop and page
 *    shadow and all, takes the thumbnail's place in the camera. It is laid out where the screen
 *    will be, so the flight ends on the editor's very pixels: the hero is simply taken away, with
 *    nothing to fade, shift or sharpen. The editor's controls come in over the hero while it
 *    flies (it sits under them), and have settled by the time it lands.
 *  - Going back starts from a copy of the editor's own canvases, so the first frame is what was on
 *    screen to the pixel — at any zoom — and fades to the whole plan as the camera pulls out.
 *  - A card's picture has round corners above and square ones below (the card goes on under it).
 *    In the air the window is round all round; its lower corners ease square as it nears the
 *    card, and are square when it is there — nothing to snap.
 *  - Two sets of floating controls are never on screen together: the screen being left fades
 *    its own out of the way first.
 *  - Glass is only ever faded as itself (app.css, THE RULE OF GLASS): an ancestor with opacity
 *    leaves the glass nothing to blur, and it snaps to frosted when the fade ends. So it is the
 *    library's CONTENT that falls back and dims, never its screen; and the controls that leave
 *    with the editor are faded pill by pill.
 *  - Hand-overs happen in the air, inside the hero, never once things have stopped. Opening, the
 *    editor's pixels take the thumbnail's place in the camera. Going back, the card's own picture
 *    fades in INSIDE the camera — registered on the plan under it, so nothing can double — with a
 *    copy of the card's tag, while the window's shadow fades to the card's: the hero lands as the
 *    card, to the pixel, and is simply taken away. Nothing fades after the movement has ended.
 *  - Everything is laid out in the END state's coordinates, however large the start: layers stay
 *    small, the GPU does the scaling.
 */
import { FIT_PAD, fitView } from '../editor/viewport.ts';
import { releaseCanvas } from '../app/image.ts';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface OpenSource {
  kind: 'open';
  id: string;
  /** The card's picture box, on screen, and its corner radius. */
  box: Rect;
  radius: number;
  /** The card's picture (already decoded: it is on screen), or null for a map without one yet. */
  image: HTMLImageElement | null;
  /** The card's tag (SOILS / CONCRETE), to be carried off with the picture: a copy of it, and where it is. */
  tag: { el: HTMLElement; box: Rect } | null;
  planW: number;
  planH: number;
}
export interface BackSource {
  kind: 'back';
  id: string;
  /** The editor's canvases as they are on screen, and the whole map drawn small. */
  shot: HTMLCanvasElement | null;
  plan: HTMLCanvasElement;
  /** Where the plan is on screen now (any zoom, any pan). */
  planRect: Rect;
  /** Clones of the editor's floating controls, to fade out as it leaves. */
  chrome: HTMLElement[];
}
type Source = OpenSource | BackSource;

/** (Development only: `__zoomSlow = 8` in the console stretches the flights, to look at them.) */
const slow = (): number => (import.meta.env.DEV ? Number((globalThis as { __zoomSlow?: number }).__zoomSlow ?? 1) || 1 : 1);
const OPEN_MS = 520;
const BACK_MS = 460;
const FRAMES = 40;
/** A spring with no bounce (critically damped), as progress 0 → 1 over the flight. */
const K = 9;
const spring = (t: number): number => (1 - (1 + K * t) * Math.exp(-K * t)) / (1 - (1 + K) * Math.exp(-K));
const lerp = (a: Rect, b: Rect, p: number): Rect => ({ x: a.x + (b.x - a.x) * p, y: a.y + (b.y - a.y) * p, w: a.w + (b.w - a.w) * p, h: a.h + (b.h - a.h) * p });
const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
};
/** Where a plan of this shape sits when it fills `box` the way a card's picture does (cover, centred). */
function coverRect(box: Rect, planW: number, planH: number): Rect {
  const a = planW / planH;
  const w = a > box.w / box.h ? box.h * a : box.w;
  const h = w / a;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

let pending: Source | null = null;
/** Taken by the router, not yet flown: the screen being mounted can still ask what it is arriving by. */
let taken: Source | null = null;
/** The flight under way: `finish` lands it at once (whatever comes next starts from a settled app). */
let flight: { finish: () => void } | null = null;
/** Set while a map is being opened: called with the editor's stage once it has drawn the plan. */
let sharpen: ((stage: HTMLElement) => void) | null = null;

const still = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches || document.visibilityState !== 'visible';

/**
 * A card was tapped: remember where it is and what it shows. Resolves when its picture is ready
 * to be drawn in the hero's first frame (a few milliseconds: it is on screen already) — the route
 * changes after that.
 */
export async function prepareOpen(id: string, card: HTMLElement, planW: number, planH: number): Promise<void> {
  finishNow(); // (a card tapped while the last flight is still landing is measured where it really is)
  pending = null;
  const thumb = card.querySelector<HTMLElement>('.thumb-img');
  if (still() || !thumb || planW <= 0 || planH <= 0) return;
  const pic = thumb.querySelector<HTMLElement>('.pic');
  const url = pic ? /url\("?([^")]+)"?\)/.exec(pic.style.backgroundImage)?.[1] : undefined;
  let image: HTMLImageElement | null = null;
  if (url) {
    image = new Image();
    image.src = url;
    const decoded = await Promise.race([image.decode().then(() => true, () => false), new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 150))]);
    if (!decoded) image = null;
  }
  const badge = card.querySelector<HTMLElement>('.badge');
  // (measured now, as the card is under the finger — its pressed state and all)
  pending = { kind: 'open', id, box: rectOf(thumb), radius: parseFloat(getComputedStyle(card).borderTopLeftRadius) || 0, image, tag: badge ? { el: badge.cloneNode(true) as HTMLElement, box: rectOf(badge) } : null, planW, planH };
}

/** Back was tapped in the editor. (The route changes next.) */
export function prepareBack(src: Omit<BackSource, 'kind'>): void {
  finishNow();
  pending = null;
  if (still()) {
    releaseSource({ kind: 'back', ...src });
    return;
  }
  pending = { kind: 'back', ...src };
}

/** True while the library is being come back to by a zoom: it arrives whole, where it was left, without its own entrance. */
export const arrivingBack = (): boolean => (taken ?? pending)?.kind === 'back';

/** What was prepared for this change of route, if anything (it is the caller's to run or to drop). */
export function take(): Source | null {
  taken = pending;
  pending = null;
  return taken;
}
export function drop(src: Source | null): void {
  taken = null;
  if (src) releaseSource(src);
}
function releaseSource(src: Source): void {
  if (src.kind === 'back') {
    if (src.shot) releaseCanvas(src.shot);
    releaseCanvas(src.plan);
  }
}

/** Land whatever is in the air, now. */
export function finishNow(): void {
  flight?.finish();
}

/** The editor has drawn its plan (editor-screen says so, having flushed the drawing): the hero takes its picture. */
export function editorReady(stage: HTMLElement): void {
  sharpen?.(stage);
}

/* ------------------------------------------------------------------------ the flight */

interface Flight {
  root: HTMLElement;
  cam: HTMLElement;
  shadow: HTMLElement;
  animations: Animation[];
}
interface Plan {
  screen: Rect;
  /** The window, and its upper corners as seen on screen (the lower ones are always square). */
  w0: Rect;
  w1: Rect;
  r0: number;
  r1: number;
  /** The plan. Everything inside the camera is laid out in the END state's coordinates (`p1`). */
  p0: Rect;
  p1: Rect;
  ms: number;
  mount: (root: HTMLElement) => void;
  layers: (cam: HTMLElement, place: (r: Rect) => Partial<CSSStyleDeclaration>) => void;
}

/**
 * Build the hero and fly it. Laid out in the END state's coordinates, the camera ends as the
 * identity (or the card's own small scale) and no layer is ever larger than it finally needs to be.
 */
function fly({ screen, w0, w1, r0, r1, p0, p1, ms, mount, layers }: Plan): Flight {
  taken = null;
  // the hero is the app's own box; inside it everything is measured from its corner
  const rel = (r: Rect): Rect => ({ x: r.x - screen.x, y: r.y - screen.y, w: r.w, h: r.h });
  [w0, w1, p0, p1] = [rel(w0), rel(w1), rel(p0), rel(p1)];
  const el = (cls: string, parent?: HTMLElement): HTMLElement => {
    const e = document.createElement('div');
    e.className = cls;
    parent?.append(e);
    return e;
  };
  const root = el('zoom');
  Object.assign(root.style, { left: `${screen.x}px`, top: `${screen.y}px`, width: `${screen.w}px`, height: `${screen.h}px` });
  const win = el('zoom-window', root);
  Object.assign(win.style, { width: `${screen.w}px`, height: `${screen.h}px` });
  const shadow = el('zoom-shadow', win);
  const clip = el('zoom-clip', win);
  const cam = el('zoom-cam', clip);
  layers(cam, (r) => ({ left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` }));

  const winFrames: Keyframe[] = [];
  const camFrames: Keyframe[] = [];
  const clipFrames: Keyframe[] = [];
  for (let i = 0; i <= FRAMES; i++) {
    const p = spring(i / FRAMES);
    const w = lerp(w0, w1, p);
    const pr = lerp(p0, p1, p);
    const sx = w.w / screen.w,
      sy = w.h / screen.h;
    // the camera: END coordinates → the screen, as one scale and a shift…
    const u = pr.w / p1.w;
    const cx = pr.x - u * p1.x,
      cy = pr.y - u * p1.y;
    winFrames.push({ transform: `translate(${w.x}px, ${w.y}px) scale(${sx}, ${sy})` });
    // …and, seen from inside the window, with the window's own transform undone
    camFrames.push({ transform: `translate(${(cx - w.x) / sx}px, ${(cy - w.y) / sy}px) scale(${u / sx}, ${u / sy})` });
    // The corners as they should look, with the window's scale undone axis by axis. The lower ones
    // are the upper ones' in the air, easing to square over the last fifth of the way to the card.
    const r = r0 + (r1 - r0) * p;
    const b = r * Math.min(1, (r0 > r1 ? p : 1 - p) / 0.22);
    clipFrames.push({ borderRadius: `${r / sx}px ${r / sx}px ${b / sx}px ${b / sx}px / ${r / sy}px ${r / sy}px ${b / sy}px ${b / sy}px` });
  }
  mount(root);
  const opts: KeyframeAnimationOptions = { duration: ms, easing: 'linear', fill: 'both' };
  return { root, cam, shadow, animations: [win.animate(winFrames, opts), cam.animate(camFrames, opts), clip.animate(clipFrames, opts), shadow.animate(clipFrames, opts)] };
}

/** The canvases of a stage, as they are, in one new canvas (null if they cannot be read). */
export function copyStage(stage: HTMLElement): HTMLCanvasElement | null {
  const base = stage.querySelector('canvas');
  if (!base || !base.width || !base.height) return null;
  try {
    const shot = document.createElement('canvas');
    shot.width = base.width;
    shot.height = base.height;
    const ctx = shot.getContext('2d')!;
    for (const c of stage.querySelectorAll('canvas')) if (c.width === shot.width && c.height === shot.height) ctx.drawImage(c, 0, 0);
    return shot;
  } catch {
    return null;
  }
}

/**
 * Library → map. `oldScreen` (the library) stays under the editor until the hero has gone;
 * `newScreen` (the editor) is mounted already: see-through, its stage unseen (and taking touches),
 * its controls coming in over the hero. `done` tears the library down.
 */
export function open(src: OpenSource, app: HTMLElement, oldScreen: HTMLElement | null, newScreen: HTMLElement, done: () => void): void {
  const screen = rectOf(app);
  const fit = fitView(src.planW, src.planH, { x: 0, y: 0, w: screen.w, h: screen.h }, FIT_PAD);
  const end: Rect = { x: screen.x + fit.tx, y: screen.y + fit.ty, w: src.planW * fit.scale, h: src.planH * fit.scale };
  newScreen.classList.add('by-zoom', 'arriving');
  const stage = newScreen.querySelector<HTMLElement>('.stage');
  const f = fly({
    screen,
    w0: src.box,
    w1: screen,
    r0: src.radius,
    r1: 0,
    p0: coverRect(src.box, src.planW, src.planH),
    p1: end,
    ms: OPEN_MS * slow(),
    // over the editor's (unseen) stage, under its controls
    mount: (root) => (stage ? stage.after(root) : newScreen.append(root)),
    layers: (cam, place) => {
      if (!src.image) return;
      src.image.className = 'zoom-plan';
      src.image.alt = '';
      Object.assign(src.image.style, place({ x: end.x - screen.x, y: end.y - screen.y, w: end.w, h: end.h }));
      cam.append(src.image);
      if (!src.tag) return;
      // the card's tag goes with its picture, fading as it goes: put where the camera shows it at
      // the start, at the size it was (the camera's first scale undone)
      const p0 = coverRect(src.box, src.planW, src.planH);
      const u0 = p0.w / end.w;
      const tag = document.createElement('div');
      tag.className = 'zoom-tag';
      Object.assign(tag.style, { left: `${end.x - screen.x + (src.tag.box.x - p0.x) / u0}px`, top: `${end.y - screen.y + (src.tag.box.y - p0.y) / u0}px`, transform: `scale(${1 / u0})` });
      tag.append(src.tag.el);
      cam.append(tag);
      tag.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120 * slow(), easing: 'ease-out', fill: 'both' }); // (gone before it has grown much)
    },
  });
  // The library's content falls back a little, and dims, as the map comes forward (its content,
  // not its screen: the glass on it must go on blurring); its floating controls go first, out
  // of the way of the map's.
  const recede = oldScreen?.querySelector('.library')?.animate([{ opacity: 1, transform: 'none' }, { opacity: 0.3, transform: 'scale(0.965)' }], { duration: OPEN_MS * slow(), easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
  for (const el of oldScreen?.querySelectorAll('.lib-top, .lib-dock') ?? []) el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 110 * slow(), easing: 'ease-out', fill: 'both' });

  // The editor has drawn: its own picture — the whole stage, where the screen will be — takes the
  // thumbnail's place. From here on the hero is the editor, to the pixel.
  let finished = false;
  let sharp: HTMLCanvasElement | null = null;
  let sharpIn: Promise<unknown> | null = null;
  let whenSharp: (() => void) | null = null;
  sharpen = (st) => {
    if (sharp || finished) return;
    sharp = copyStage(st);
    if (!sharp) return;
    const r = rectOf(st);
    sharp.className = 'zoom-sharp';
    Object.assign(sharp.style, { left: `${r.x - screen.x}px`, top: `${r.y - screen.y}px`, width: `${r.w}px`, height: `${r.h}px` });
    f.cam.append(sharp);
    sharpIn = sharp.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 110 * slow(), easing: 'ease-out', fill: 'both' }).finished.catch(() => {});
    whenSharp?.();
  };

  let failsafe = 0;
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(failsafe);
    flight = null;
    sharpen = null;
    // in one breath: the editor is shown and the hero — the same pixels — is gone
    newScreen.classList.remove('arriving');
    f.root.remove();
    for (const a of f.animations) a.cancel();
    recede?.cancel();
    if (sharp) releaseCanvas(sharp);
    done();
  };
  flight = { finish };
  void f.animations[0]!.finished
    .then(async () => {
      if (finished) return;
      // landed before the editor had drawn (a slow device, a plan still to be fetched): hold, as the thumbnail, until it has
      if (!sharp) await new Promise<void>((resolve) => (whenSharp = resolve));
      await sharpIn;
      finish();
    })
    .catch(() => finish());
  failsafe = window.setTimeout(finish, 6000 * slow());
}

/** Map → library. `newScreen` (the library) is mounted already; the editor is gone. */
export function back(src: BackSource, app: HTMLElement, newScreen: HTMLElement): void {
  const screen = rectOf(app);
  const onBody = (root: HTMLElement) => document.body.append(root);
  const card = newScreen.querySelector<HTMLElement>(`.card[data-id="${src.id}"]`);
  const thumb = card?.querySelector<HTMLElement>('.thumb-img') ?? null;
  if (!card || !thumb) {
    // no card to go back into (the map is gone): the plan simply falls away
    const ghost = fly({ screen, w0: screen, w1: screen, r0: 0, r1: 0, p0: src.planRect, p1: src.planRect, ms: 1, mount: onBody, layers: (cam, place) => addBackLayers(cam, place, src, screen, src.planRect, src.planRect) });
    ghost.root.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.94)' }], { duration: 260, easing: 'ease-out', fill: 'both' }).finished.then(
      () => (ghost.root.remove(), releaseSource(src)),
      () => (ghost.root.remove(), releaseSource(src)),
    );
    return;
  }
  // where it was left; then where that is (measured before anything moves)
  card.scrollIntoView({ block: 'nearest' });
  const box = rectOf(thumb);
  // (the tag too, now: measured later it would be measured under the library's content coming forward — a hair out, and the tag would jump on landing)
  const badge = card.querySelector<HTMLElement>('.badge');
  const tagBox = badge ? rectOf(badge) : null;
  const radius = parseFloat(getComputedStyle(card).borderTopLeftRadius) || 0;
  const end = coverRect(box, src.planRect.w, src.planRect.h);
  card.classList.add('landing');
  const f = fly({ screen, w0: screen, w1: box, r0: 0, r1: radius, p0: src.planRect, p1: end, ms: BACK_MS * slow(), mount: onBody, layers: (cam, place) => addBackLayers(cam, place, src, screen, src.planRect, end) });
  const chrome = document.createElement('div');
  chrome.className = 'zoom-chrome';
  chrome.append(...src.chrome);
  f.root.append(chrome);
  const ms = BACK_MS * slow();
  // the editor's controls go pill by pill (each piece of glass itself, never what holds it)
  const pills = src.chrome.flatMap((el) => (el.matches('.glass, .tinted, .scrim') ? [el] : [...el.querySelectorAll<HTMLElement>('.glass, .tinted')]));
  const content = newScreen.querySelector<HTMLElement>('.library') ?? newScreen;
  const others = [
    ...pills.map((el) => el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140 * slow(), easing: 'ease-out', fill: 'both' })),
    // what was on screen, to the pixel, gives way to the whole plan as the camera pulls out
    ...(src.shot ? [src.shot.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms * 0.4, easing: 'ease-out', fill: 'both' })] : []),
    // the library's content comes forward; its own floating controls come in once the map's have gone
    content.animate([{ opacity: 0.3, transform: 'scale(0.965)' }, { opacity: 1, transform: 'none' }], { duration: ms, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }),
    ...[...newScreen.querySelectorAll('.lib-top, .lib-dock')].map((el) => el.animate([{ opacity: 0 }, { opacity: 1 }], { delay: 110 * slow(), duration: 260 * slow(), easing: 'ease-out', fill: 'both' })),
  ];

  let finished = false;
  let handOver = 0;
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(handOver);
    flight = null;
    // in one breath: the card shows its picture and its tag, and the hero — the same, in the same place — is gone
    card.classList.remove('landing');
    f.root.remove();
    for (const a of [...f.animations, ...others]) a.cancel();
    releaseSource(src);
  };
  flight = { finish };
  // The hand-over, in the air: from a little before half way — the window moving fast — the card's
  // own picture (the map as it now is, saved on the way out) fades in inside the camera, exactly
  // over the plan; then a copy of its tag; and the window's shadow fades towards the card's.
  // (Should the card's picture not be there yet, the hand-over waits for it — on the ground, if it must.)
  const newest = (): string | undefined => /url\("?([^")]+)"?\)/.exec([...thumb.querySelectorAll<HTMLElement>('.pic')].pop()?.style.backgroundImage ?? '')?.[1];
  let used: string | undefined;
  const handedOver = new Promise<void>((resolve) => {
    const begin = (tries: number) => {
      if (finished) return resolve();
      used = newest();
      if (!used && tries < 60) {
        handOver = window.setTimeout(() => begin(tries + 1), 16);
        return;
      }
      const waits: Promise<unknown>[] = [f.shadow.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ms * 0.5, easing: 'ease-in-out', fill: 'both' }).finished];
      if (used) {
        const pic = document.createElement('div');
        pic.className = 'zoom-pic';
        Object.assign(pic.style, { left: `${box.x - screen.x}px`, top: `${box.y - screen.y}px`, width: `${box.w}px`, height: `${box.h}px`, backgroundImage: `url("${used}")` });
        f.cam.append(pic);
        waits.push(pic.animate([{ opacity: 0 }, { opacity: 1 }], { duration: ms * 0.3, easing: 'ease-in-out', fill: 'both' }).finished);
      }
      if (badge && tagBox) {
        const tag = document.createElement('div');
        tag.className = 'zoom-tag';
        Object.assign(tag.style, { left: `${tagBox.x - screen.x}px`, top: `${tagBox.y - screen.y}px` });
        tag.append(badge.cloneNode(true));
        f.cam.append(tag);
        waits.push(tag.animate([{ opacity: 0 }, { opacity: 1 }], { delay: ms * 0.1, duration: ms * 0.34, easing: 'ease-in-out', fill: 'both' }).finished);
      }
      void Promise.all(waits).then(() => resolve(), () => resolve());
    };
    handOver = window.setTimeout(() => begin(0), ms * 0.42);
  });
  void Promise.all([f.animations[0]!.finished, handedOver])
    .then(async () => {
      // (the card took a newer picture after the hero took its own: only then is there anything to dissolve)
      if (!finished && newest() !== used) await f.root.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease-out', fill: 'both' }).finished;
    })
    .then(finish, finish);
}

/** The whole plan, and over it what was on screen — both in the END state's coordinates. */
function addBackLayers(cam: HTMLElement, place: (r: Rect) => Partial<CSSStyleDeclaration>, src: BackSource, screen: Rect, start: Rect, end: Rect): void {
  src.plan.className = 'zoom-plan';
  Object.assign(src.plan.style, place({ x: end.x - screen.x, y: end.y - screen.y, w: end.w, h: end.h }));
  cam.append(src.plan);
  if (!src.shot) return;
  // the screen as it was, carried into END coordinates by the camera's starting position, undone
  const u0 = start.w / end.w;
  src.shot.className = 'zoom-shot';
  Object.assign(src.shot.style, place({ x: end.x - screen.x - (start.x - screen.x) / u0, y: end.y - screen.y - (start.y - screen.y) / u0, w: screen.w / u0, h: screen.h / u0 }));
  cam.append(src.shot);
}
